import { useMemo } from "react";
import React from "react";
import { HeatmapRow, formatTemp, formatTime, tempToColor } from "./ThermalHeatMapHooks";
import { AlertEvent, getSubsystemsForFace, getThresholdValue } from "./useThermalAlerts";
import { PdfReportData, SubsystemAnalysis } from "../components/exportpdf/pdfExportTypes";
import type { ThermalHeatMap3dViewHandle } from "../components/visualization/ThermalHeatMap3dView";
import { FACE_ORDER, INT_ORDER } from "../constants/thermalConstants";
import { getPdfDeckLabel } from "../components/exportpdf/pdfExportConstants";
import { getSubsystemThresholds, getPanelThresholds, getFacePanelLabel } from "../constants/surfaceThresholdsData";
import type { ScheduleTaskType } from "../../../../utils/scenario-types";
import type { ThresholdConfig } from "../components/panels/ThermalAlertThresholds";

interface ExposureTiming {
  exposureType: 'Eclipse' | 'Both' | 'Sunlit';
  startTime: number;
  endTime: number;
  duration: number;
}

// ────────────────────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS
// ────────────────────────────────────────────────────────────────────────────

/**
 * Calculate basic statistics (min, max, average) for a temperature array.
 */
export function calculateStatistics(temps: number[]): { min: number; max: number; avg: number } {
  if (temps.length === 0) {
    return { min: 0, max: 0, avg: 0 };
  }

  let min = temps[0];
  let max = temps[0];
  let sum = 0;

  for (const temp of temps) {
    if (temp < min) min = temp;
    if (temp > max) max = temp;
    sum += temp;
  }

  return {
    min,
    max,
    avg: sum / temps.length,
  };
}

/**
 * Check if a node exists in at least one heatmap row (has real data).
 */
function nodeHasData(nodeIds: string[], heatmapRows: HeatmapRow[]): boolean {
  for (const row of heatmapRows) {
    for (const nodeId of nodeIds) {
      if (row.nodes[nodeId] !== undefined) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Extract temperature values for a given set of node IDs from heatmap rows.
 * Averages the given nodes per row, returning an array of average temperatures per row.
 * Only includes rows where at least one node has data; excludes missing nodes.
 */
export function extractSubsystemTemperature(
  nodeIds: string[],
  heatmapRows: HeatmapRow[]
): number[] {
  const result: number[] = [];
  for (const row of heatmapRows) {
    let sum = 0;
    let count = 0;

    for (const nodeId of nodeIds) {
      const temp = row.nodes[nodeId];
      if (temp !== undefined && !Number.isNaN(temp)) {
        sum += temp;
        count++;
      }
    }

    // Only include rows where at least one node has data
    if (count > 0) {
      result.push(sum / count);
    }
  }
  return result;
}

/**
 * Find the peak (maximum) and valley (minimum) temperatures across heatmap rows,
 * averaging the given node IDs at each row.
 */
export interface PeakValleyResult {
  peakIndex: number;
  valleyIndex: number;
  peakTime: number;
  valleyTime: number;
  peakTemp: number;
  valleyTemp: number;
}

export function findPeakAndValley(
  heatmapRows: HeatmapRow[],
  nodeIds: string[]
): PeakValleyResult {
  if (heatmapRows.length === 0 || nodeIds.length === 0) {
    return {
      peakIndex: 0,
      valleyIndex: 0,
      peakTime: 0,
      valleyTime: 0,
      peakTemp: 0,
      valleyTemp: 0,
    };
  }

  let peakIndex = 0;
  let valleyIndex = 0;
  let peakTemp = -Infinity;
  let valleyTemp = Infinity;

  for (let i = 0; i < heatmapRows.length; i++) {
    const row = heatmapRows[i];
    let sum = 0;
    let count = 0;

    for (const nodeId of nodeIds) {
      const temp = row.nodes[nodeId];
      if (temp !== undefined && !Number.isNaN(temp)) {
        sum += temp;
        count++;
      }
    }

    const avgTemp = count > 0 ? sum / count : 0;

    if (avgTemp > peakTemp) {
      peakTemp = avgTemp;
      peakIndex = i;
    }

    if (avgTemp < valleyTemp) {
      valleyTemp = avgTemp;
      valleyIndex = i;
    }
  }

  return {
    peakIndex,
    valleyIndex,
    peakTime: heatmapRows[peakIndex].time_sec,
    valleyTime: heatmapRows[valleyIndex].time_sec,
    peakTemp,
    valleyTemp,
  };
}

/**
 * Format duration in seconds to human-readable string (e.g., "2h 15m 30s").
 */
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(" ");
}

/**
 * Get thermal gradient color for a temperature within a given range.
 * Delegates to the existing tempToColor function from ThermalHeatMapHooks.
 */
export function getThermalGradientColor(temp: number, min: number, max: number): string {
  return tempToColor(temp, min, max);
}

/**
 * Select evenly-spaced indices (for downsampling).
 * Shared helper to avoid duplicating stride math.
 */
function selectDownsampleIndices(dataLength: number, maxPoints: number): number[] {
  if (dataLength <= maxPoints) return Array.from({ length: dataLength }, (_, i) => i);
  const step = (dataLength - 1) / (maxPoints - 1);
  return Array.from({ length: maxPoints }, (_, i) => Math.round(i * step));
}

/**
 * Downsample an array to at most maxPoints, picking evenly-spaced points.
 * Always includes the first and last points. For chart rendering only,
 * not for accuracy-sensitive calculations like peak/valley detection.
 */
export function downsampleSeries(data: number[], maxPoints: number): number[] {
  if (data.length <= maxPoints) return data;
  const indices = selectDownsampleIndices(data.length, maxPoints);
  return indices.map(i => data[i]);
}

/**
 * Downsample times and values in lockstep, returning paired {x, y} points.
 * x is the time value (elapsed seconds), y is the corresponding data value.
 * Ensures the same indices are selected from both arrays.
 */
export function downsampleTimeSeries(
  times: number[],
  values: number[],
  maxPoints: number
): Array<{ x: number; y: number }> {
  if (times.length !== values.length) {
    throw new Error("times and values arrays must have the same length");
  }
  if (times.length <= maxPoints) {
    return times.map((t, i) => ({ x: t, y: values[i] }));
  }
  const indices = selectDownsampleIndices(times.length, maxPoints);
  return indices.map(i => ({ x: times[i], y: values[i] }));
}

/**
 * Build enriched alert data with face/severity/temperature/threshold/mission-phase/analysis/recommendation.
 * Completes all the mapping that was previously split between mapAlertsToSubsystems and later tasks.
 */
export function buildEnrichedAlerts(alerts: AlertEvent[], thresholds: ThresholdConfig[], sunExposures: ExposureTiming[], t0Epoch: number | null | undefined): PdfReportData["alerts"] {
  return alerts.map((alert) => {
    const thresholdValue = getThresholdValue(alert, thresholds);

    const epochTime = (t0Epoch ?? 0) + alert.timeSec;
    const exposure = sunExposures.find(
      (e) => epochTime >= e.startTime && epochTime <= e.endTime
    );
    const missionPhaseMap: Record<string, "SUNLIT" | "ECLIPSE" | "TRANSITION"> = {
      "Sunlit": "SUNLIT",
      "Eclipse": "ECLIPSE",
      "Both": "TRANSITION",
    };
    const missionPhase = exposure ? missionPhaseMap[exposure.exposureType] ?? "SUNLIT" : "SUNLIT";

    const subsystemsOnFace = getSubsystemsForFace(alert.face);
    const contributingSubsystems = subsystemsOnFace.map((subsys) => {
      if (alert.subsystemName && subsys.subsystemName === alert.subsystemName) {
        return `${subsys.subsystemName} (out of range)`;
      }
      return alert.subsystemName ? `${subsys.subsystemName} (nominal)` : subsys.subsystemName;
    });

    const tempDelta = Math.abs(alert.peakTemp - thresholdValue);
    const breachDirection = alert.breach === "high" ? "exceeded" : "fell below";
    const analysis = `Peak temperature of ${alert.peakTemp.toFixed(1)}°C ${breachDirection} the ${thresholdValue.toFixed(1)}°C threshold by ${tempDelta.toFixed(1)}°C during the ${formatTime(alert.timeSec)}–${formatTime(alert.endTimeSec)} window.`;

    const recommendation =
      alert.breach === "high"
        ? `Consider increasing radiator area on ${alert.faceLabel}.`
        : `Consider additional heating on ${alert.faceLabel}.`;

    return {
      face: alert.faceLabel,
      severity: alert.severity === "critical" ? "CRITICAL" : "WARNING",
      temperature: alert.peakTemp,
      threshold: thresholdValue,
      startTime: formatTime(alert.timeSec),
      endTime: formatTime(alert.endTimeSec),
      durationSeconds: alert.endTimeSec - alert.timeSec,
      missionPhase,
      contributingSubsystems,
      analysis,
      recommendation,
    };
  });
}

// Re-export existing utilities for convenience
export { formatTemp, formatTime, tempToColor };

// ────────────────────────────────────────────────────────────────────────────
// REACT HOOKS
// ────────────────────────────────────────────────────────────────────────────

/**
 * Hook to capture 3D view snapshots at specific animation frames.
 * Drives the 3D view to a target step index, waits for the render to land,
 * then captures the canvas as a PNG data URL.
 */
export function use3dSnapshot(viewRef: React.RefObject<ThermalHeatMap3dViewHandle>) {
  return {
    captureAtStep: async (
      stepIndex: number,
      setStepIndex: (i: number) => void
    ): Promise<string | null> => {
      // Drive the 3D view to the target step
      setStepIndex(stepIndex);

      // Wait for two animation frames to ensure the render completes
      return new Promise((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const snapshot = viewRef.current?.captureSnapshot();
            resolve(snapshot || null);
          });
        });
      });
    },
  };
}

export interface PdfDataBuilderInput {
  heatmapRows: HeatmapRow[];
  nodesPerFace: number;
  envParams: { albedo_factor: number | null; blackbody_temperature: number | null; solar_constant: number | null };
  alerts: AlertEvent[];
  thermalModel?: any;
  scenarioResponse?: any;
  selectedGroupSatellite?: any;
  orbitalInsights?: any;
  t0Epoch?: number | null;
  thresholds: ThresholdConfig[];
  sunExposures: ExposureTiming[];
}

// Pure builder: called lazily from the export click handler (not on every render).
export function buildPdfData(input: PdfDataBuilderInput): PdfReportData {
  const { heatmapRows, nodesPerFace, envParams, alerts, thermalModel, scenarioResponse, selectedGroupSatellite, orbitalInsights, t0Epoch, thresholds, sunExposures } = input;

  const pdfData = ((): PdfReportData => {
    // Extract all temperatures from heatmap rows
    const allTemps: number[] = [];
    for (const row of heatmapRows) {
      for (const temp of Object.values(row.nodes)) {
        if (typeof temp === "number" && !Number.isNaN(temp)) {
          allTemps.push(temp);
        }
      }
    }

    const stats = calculateStatistics(allTemps);

    const now = new Date().toLocaleDateString();

    // Calculate simulation duration (in seconds)
    const durationSec = heatmapRows.length > 0
      ? heatmapRows[heatmapRows.length - 1].time_sec
      : 0;

    // Resolve satellite details from scenarioResponse
    let satelliteName = "—";
    let orbitalParameters = { altitude: 0, eccentricity: 0, inclination: 0, RAAN: 0, AP: 0, TA: 0 };
    if (selectedGroupSatellite && scenarioResponse?.satelliteGroups) {
      const group = scenarioResponse.satelliteGroups.find((g: any) => g.satelliteGroupId === selectedGroupSatellite.groupId);
      if (group?.satellites) {
        const satellite = group.satellites.find((s: any) => s.satelliteId === selectedGroupSatellite.satelliteId);
        if (satellite) {
          satelliteName = satellite.satelliteName || "—";
          if (satellite.orbitParameters) {
            orbitalParameters = {
              altitude: satellite.orbitParameters.altitude ?? 0,
              eccentricity: satellite.orbitParameters.eccentricity ?? 0,
              inclination: satellite.orbitParameters.inclination ?? 0,
              RAAN: satellite.orbitParameters.RAAN ?? 0,
              AP: satellite.orbitParameters.AP ?? 0,
              TA: satellite.orbitParameters.TA ?? 0,
            };
          }
        }
      }
    }

    // Compute start/end times from t0Epoch
    let startTimeFormatted = "—";
    let endTimeFormatted = "—";
    if (t0Epoch !== null && t0Epoch !== undefined) {
      const startDate = new Date(t0Epoch * 1000);
      const endDate = new Date((t0Epoch + durationSec) * 1000);
      startTimeFormatted = startDate.toLocaleString();
      endTimeFormatted = endDate.toLocaleString();
    }

    // Extract scenario and orbit coverage data
    const scenarioName = scenarioResponse?.scenarioName || "—";
    const dataResolutionSec = scenarioResponse?.sampleRate ?? 0;
    const orbitsCovered = orbitalInsights?.[selectedGroupSatellite?.satelliteId]?.orbitsByOrbitalPeriod?.length ?? 0;

    // Get environment parameters
    const solarConstant = envParams?.solar_constant ?? 1361;
    const albedoFactor = envParams?.albedo_factor ?? 0.3;
    const blackbodyTemp = envParams?.blackbody_temperature ?? 288.15;

    // Build face analysis data for each face and internal deck
    const allFaceKeys = [...FACE_ORDER, ...INT_ORDER];
    const decksAnalyzed = allFaceKeys.length;
    const faces = allFaceKeys.map((faceId) => {
      const nodeIds = Array.from({ length: nodesPerFace }, (_, i) => `${faceId}_N${i + 1}`);
      const peakValley = findPeakAndValley(heatmapRows, nodeIds);
      const faceTemps = extractSubsystemTemperature(nodeIds, heatmapRows);
      const faceStats = calculateStatistics(faceTemps);

      // Count violations for this face/deck
      const faceViolations = alerts.filter(
        (alert) => alert.face?.toUpperCase() === faceId.toUpperCase()
      ).length;

      // Look up real face/deck thresholds; use thermalDesign tier (matches live Safe Margin UI)
      const panelLabel = FACE_ORDER.includes(faceId as any) ? getFacePanelLabel(faceId as any) : faceId;
      const panelThresholds = getPanelThresholds(thermalModel, panelLabel);
      const thresholdMin = panelThresholds.thermalDesign.min ?? null;
      const thresholdMax = panelThresholds.thermalDesign.max ?? null;

      // Check if this face/deck has real data
      const faceHasData = nodeHasData(nodeIds, heatmapRows);

      return {
        faceId,
        displayName: getPdfDeckLabel(faceId),
        stats: faceStats,
        threshold: {
          min: thresholdMin,
          max: thresholdMax,
        },
        // peakSnapshot: "", // 3D snapshot capture removed; per-face pages now chart-only
        timeseriesChart: "",
        violations: faceViolations,
        temps: faceTemps,
        peakIndex: peakValley.peakIndex,
        valleyIndex: peakValley.valleyIndex,
        hasData: faceHasData,
      };
    });

    const times = heatmapRows.map(r => r.time_sec);

    // Build subsystem analysis data for each face/deck
    const subsystems: SubsystemAnalysis[] = [];
    for (const faceId of allFaceKeys) {
      const subsystemsForFace = getSubsystemsForFace(faceId);
      for (const subsystem of subsystemsForFace) {
        const nodeIds = [subsystem.nodeId];
        const subsystemTemps = extractSubsystemTemperature(nodeIds, heatmapRows);
        const subsystemStats = calculateStatistics(subsystemTemps);

        // Look up real subsystem thresholds; use thermalDesign tier (matches live Safe Margin UI)
        const subsystemThresholds = getSubsystemThresholds(thermalModel, subsystem.subsystemName);
        const thresholdMin = subsystemThresholds.thermalDesign.min ?? null;
        const thresholdMax = subsystemThresholds.thermalDesign.max ?? null;

        // Check if this subsystem has real data
        const hasData = nodeHasData(nodeIds, heatmapRows);

        // Determine if there's a breach (temperature exceeds threshold), only if data exists
        let peakBreach: number | undefined;
        if (hasData && thresholdMin !== null && thresholdMax !== null) {
          peakBreach =
            subsystemStats.max > thresholdMax
              ? subsystemStats.max - thresholdMax
              : subsystemStats.min < thresholdMin
              ? thresholdMin - subsystemStats.min
              : undefined;
        }

        subsystems.push({
          subsystemId: subsystem.nodeId,
          displayName: subsystem.subsystemName,
          deck: getPdfDeckLabel(faceId),
          stats: subsystemStats,
          threshold: {
            min: thresholdMin,
            max: thresholdMax,
          },
          timeseriesChart: "",
          peakBreach,
          hasData,
        });
      }
    }

    // Calculate critical alert count
    const criticalAlertCount = alerts.filter(a => a.severity === "critical").length;

    return {
      metadata: {
        simulationDate: now,
        analysisDate: now,
        durationSec,
        satelliteName,
        startTimeFormatted,
        endTimeFormatted,
        scenarioName,
        dataResolutionSec,
        orbitsCovered,
        decksAnalyzed,
        orbitalParameters,
      },
      summary: {
        maxTemp: stats.max,
        minTemp: stats.min,
        avgTemp: stats.avg,
        violationCount: alerts.length,
        minMargin: 0, // TODO: calculated from alerts when available
        criticalAlertCount,
      },
      faces,
      subsystems,
      alerts: buildEnrichedAlerts(alerts, thresholds, sunExposures, t0Epoch),
      environment: {
        solarConstant,
        albedoFactor,
        blackbodyTemp,
      },
      times,
    };
  })();

  return pdfData;
}

// Hook wrapper kept for compatibility; no longer used (export now calls buildPdfData on click).
export function usePdfDataBuilder(input: PdfDataBuilderInput): PdfReportData {
  return useMemo(() => buildPdfData(input), [input]);
}

/**
 * Paginate subsystems, grouping by deck to avoid splitting a deck awkwardly.
 * Uses strict 4 per page, even if a page contains subsystems from only one deck.
 */
export function paginateSubsystems(subsystems: SubsystemAnalysis[], perPage: number = 4): SubsystemAnalysis[][] {
  const pages: SubsystemAnalysis[][] = [];
  let currentPage: SubsystemAnalysis[] = [];

  for (const subsystem of subsystems) {
    currentPage.push(subsystem);
    if (currentPage.length === perPage) {
      pages.push(currentPage);
      currentPage = [];
    }
  }

  if (currentPage.length > 0) {
    pages.push(currentPage);
  }

  return pages;
}

/**
 * Paginate alerts for PDF pages. Simple sequential chunking, 6 per page.
 */
export function paginateAlerts(alerts: PdfReportData["alerts"], perPage: number = 6): Array<PdfReportData["alerts"]> {
  const pages: Array<PdfReportData["alerts"]> = [];
  let currentPage: PdfReportData["alerts"] = [];

  for (const alert of alerts) {
    currentPage.push(alert);
    if (currentPage.length === perPage) {
      pages.push(currentPage);
      currentPage = [];
    }
  }

  if (currentPage.length > 0) {
    pages.push(currentPage);
  }

  return pages;
}

/**
 * Paginate simulation temperature rows (subsystems) into fixed-size chunks (20 per page).
 * Returns empty array if there are no subsystems, so the page is skipped entirely.
 */
export function paginateSimTempRows(subsystems: SubsystemAnalysis[], perPage: number = 20): SubsystemAnalysis[][] {
  if (subsystems.length === 0) return [];

  const pages: SubsystemAnalysis[][] = [];
  let currentPage: SubsystemAnalysis[] = [];

  for (const subsystem of subsystems) {
    currentPage.push(subsystem);
    if (currentPage.length === perPage) {
      pages.push(currentPage);
      currentPage = [];
    }
  }

  if (currentPage.length > 0) {
    pages.push(currentPage);
  }

  return pages;
}

/**
 * Paginate schedule tasks into fixed-size chunks (14 per page).
 * Sorts tasks by startTime ascending once before chunking.
 * Always returns at least one page (possibly empty) so the Schedule section
 * can render its "No scheduled tasks" placeholder.
 */
export function paginateScheduleTasks(tasks: ScheduleTaskType[], perPage: number = 14): ScheduleTaskType[][] {
  const sorted = [...tasks].sort((a, b) => (a.startTime ?? 0) - (b.startTime ?? 0));
  const pages: ScheduleTaskType[][] = [];
  let currentPage: ScheduleTaskType[] = [];

  for (const task of sorted) {
    currentPage.push(task);
    if (currentPage.length === perPage) {
      pages.push(currentPage);
      currentPage = [];
    }
  }

  if (currentPage.length > 0) {
    pages.push(currentPage);
  }

  return pages.length > 0 ? pages : [[]];
}
